import { useEffect, useRef, useState } from 'react';
import { type AnyChainflipAsset } from '@chainflip/utils/chainflip';
import classNames from 'classnames';
import { motion } from 'motion/react';
import Switch2Icon from '@/shared/icons/flip-ui-kit/small/Switch2';
import { anyChainflipAssetMap } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';

export const Rate = ({
  depositAmount,
  sourceAsset,
  egressAmount,
  destinationAsset,
  className,
  decimalCap = 8,
  setInverted,
}: {
  depositAmount: string;
  sourceAsset: AnyChainflipAsset;
  egressAmount: string;
  destinationAsset: AnyChainflipAsset;
  className?: string;
  decimalCap?: number;
  setInverted?: (inverted: boolean) => void;
}) => {
  const [showInverseRate, setShowInverseRate] = useState(false);
  const initialRender = useRef(true);

  useEffect(() => {
    setInverted?.(showInverseRate);
  }, [showInverseRate]);

  const onSwitch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    initialRender.current = false;
    setShowInverseRate(!showInverseRate);
  };

  const switchAnimate = () => {
    if (initialRender.current) return 'none';
    if (!showInverseRate) return 'flash';
    return 'flash2';
  };

  const deposit = TokenAmount.fromAsset(depositAmount, sourceAsset);
  const egress = TokenAmount.fromAsset(egressAmount, destinationAsset);
  const rate = showInverseRate ? deposit.ratio(egress) : egress.ratio(deposit);

  return (
    <motion.button
      animate={switchAnimate()}
      variants={{
        none: { opacity: 1 },
        flash: { opacity: [0, 1] },
        flash2: { opacity: [0, 1] },
      }}
      transition={{ duration: 0.15 }}
      className={classNames(
        `flex items-center gap-x-1 text-cf-light-3 transition duration-150 ease-in hover:text-white`,
        className,
      )}
      onClick={onSwitch}
    >
      <span className="flex items-center">
        {rate.isNaN() ? '' : 1}{' '}
        {showInverseRate
          ? anyChainflipAssetMap[destinationAsset].symbol
          : anyChainflipAssetMap[sourceAsset].symbol}
      </span>
      <Switch2Icon className="cursor-pointer" />
      <span className="flex items-center">
        {rate.isNaN() ? '' : rate.toFormat(decimalCap)}{' '}
        {showInverseRate
          ? anyChainflipAssetMap[sourceAsset].symbol
          : anyChainflipAssetMap[destinationAsset].symbol}
      </span>
    </motion.button>
  );
};
